import { useEffect } from 'react';

interface SeoHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  jsonLd?: object | object[];
}

export default function SeoHead({ title, description, keywords, canonical, jsonLd }: SeoHeadProps) {
  useEffect(() => {
    // Title
    document.title = title;

    // Description
    let descEl = document.querySelector('meta[name="description"]');
    if (!descEl) {
      descEl = document.createElement('meta');
      (descEl as HTMLMetaElement).name = 'description';
      document.head.appendChild(descEl);
    }
    (descEl as HTMLMetaElement).content = description;

    // Keywords
    if (keywords) {
      let kwEl = document.querySelector('meta[name="keywords"]');
      if (!kwEl) {
        kwEl = document.createElement('meta');
        (kwEl as HTMLMetaElement).name = 'keywords';
        document.head.appendChild(kwEl);
      }
      (kwEl as HTMLMetaElement).content = keywords;
    }

    // Canonical
    if (canonical) {
      let canonEl = document.querySelector('link[rel="canonical"]');
      if (!canonEl) {
        canonEl = document.createElement('link');
        (canonEl as HTMLLinkElement).rel = 'canonical';
        document.head.appendChild(canonEl);
      }
      (canonEl as HTMLLinkElement).href = canonical;
    }

    // OG tags
    const ogTags: Record<string, string> = {
      'og:title': title,
      'og:description': description,
    };
    if (canonical) ogTags['og:url'] = canonical;

    Object.entries(ogTags).forEach(([property, content]) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement('meta');
        (el as HTMLMetaElement).setAttribute('property', property);
        document.head.appendChild(el);
      }
      (el as HTMLMetaElement).content = content;
    });

    // Twitter tags
    const twitterTags: Record<string, string> = {
      'twitter:title': title,
      'twitter:description': description,
    };
    Object.entries(twitterTags).forEach(([name, content]) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        (el as HTMLMetaElement).name = name;
        document.head.appendChild(el);
      }
      (el as HTMLMetaElement).content = content;
    });

    // JSON-LD
    const existingScripts = document.querySelectorAll('script[data-seo-jsonld]');
    existingScripts.forEach(s => s.remove());

    if (jsonLd) {
      const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      schemas.forEach(schema => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-jsonld', 'true');
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);
      });
    }

    return () => {
      // Cleanup JSON-LD on unmount
      document.querySelectorAll('script[data-seo-jsonld]').forEach(s => s.remove());
    };
  }, [title, description, keywords, canonical, jsonLd]);

  return null;
}
